import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button } from 'react-bootstrap';
import Breadcrumbs from '../../../Common/Breadcrumbs';
import ShimmerLoader from '../../../Common/ShimmerLoader';
import { getOrderDetails, markOrdersReadyForPickup, markOrdersPickedUp, cancelOrder } from '../../../api/shop/orders/orderService';
import { OrderDetailData, OrderStatusHistoryItem, OrderItemDetail } from '../../../api/shop/types/orderTypes';
import { OrderStatusBadge } from '../../../Common/BadgeStyles';
import secureApi from '../../../utils/secureApi';
import { SHOP_ENDPOINTS } from '../../../api/shop/endpoints';
import { getImageUrl } from '../../../utils/imageUrl';
import { formatDate } from '../../../utils/formatUtils';
import TrackOrder from './components/TrackOrder';
import OrderItems from './components/OrderItems';
import ContactInformation from './components/ContactInformation';
import OrderPickupDetails from './components/OrderPickupDetails';
import OtherOrderItems from './components/OtherOrderItems';
import { MarkReadyModal } from './components/MarkReadyModal';
import CancelOrderModal from './components/CancelOrderModal';
import { useSelector } from 'react-redux';
import { hasAnyPermission, type User } from '../../../utils/roleBasedAccess';
import './OrderDetail.css';

const OrderDetail: React.FC = () => {
  const { orderId, orderItemId } = useParams<{ orderId: string; orderItemId: string }>();
  const navigate = useNavigate();
  const { user, role, permissions } = useSelector((state: any) => state.Login);
  const userData: User = useMemo(
    () => ({
      ...(user || {}),
      subRole: (user && user.subRole) || role,
      permissions: permissions,
    }),
    [user, role, permissions]
  );
  const hasManagePermissions = hasAnyPermission(userData, [
    'shop.admin',
    'shop.order.manage',
  ]);
  const [orderDetail, setOrderDetail] = useState<OrderDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  
  // Action button states
  const [showActionModal, setShowActionModal] = useState(false);
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  
  // Cancel order states
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const fetchOrderDetail = async (isRetry = false) => {
    if (!orderId || !orderItemId) {
      setError('Missing order ID or order item ID');
      setLoading(false);
      return;
    }
    
    try {
      if (isRetry) {
        setRetrying(true);
      } else {
        setLoading(true);
      }
      setError(null);
      
      const response = await getOrderDetails(orderId, orderItemId);
      
      // Validate response structure
      if (!response || !response.data) {
        throw new Error('Invalid response format');
      }
      
      // Validate essential order data
      if (!response.data.orderItems || response.data.orderItems.length === 0) {
        throw new Error('No order items found');
      }
      
      // Check if order has returnRequest (any status) and redirect to return detail page if needed
      // This is a fallback for direct URL access - listing level routing handles this normally
      const orderData = response.data as any;
      
      // Priority 1: Check for return request first
      // Check multiple possible locations for return information
      let returnId: string | null = null;
      
      // Check order level returnRequest
      if (orderData.returnRequest) {
        returnId = orderData.returnRequest.returnId || orderData.returnId || null;
      }
      
      // Check orderItemInfo for returnRequestId or returnStatus (most common in API)
      if (!returnId && orderData.orderItemInfo) {
        returnId = 
          orderData.orderItemInfo.returnRequestId ||
          orderData.orderItemInfo.returnId ||
          orderData.orderItemInfo.returnRequest?.returnId ||
          orderData.orderItemInfo.returnRequest?._id ||
          null;
        
        // Also check if returnStatus exists (indicates item has been returned)
        const hasReturnStatus = orderData.orderItemInfo.returnStatus && 
          orderData.orderItemInfo.returnStatus !== null && 
          orderData.orderItemInfo.returnStatus !== undefined;
        
        // If returnStatus exists but returnId wasn't found, try to get it from returnRequest
        if (hasReturnStatus && !returnId && orderData.orderItemInfo.returnRequest) {
          returnId = 
            orderData.orderItemInfo.returnRequest.returnId ||
            orderData.orderItemInfo.returnRequest._id ||
            orderData.orderItemInfo.returnRequest.id ||
            null;
        }
      }
      
      // If returnId found, redirect to return detail page
      if (returnId) {
        navigate(`/shop-services/returns/${returnId}`, { replace: true });
        return;
      }
      
      // Priority 2: Check if order is cancelled and redirect to cancellation detail page
      // Check multiple possible locations for cancellation status:
      // 1. orderItemInfo.cancellation (preserved from API response)
      // 2. orderItemInfo.status === 'CANCELLED'
      // 3. orderItems[0].orderStatusHistory contains CANCELLED status
      const hasCancellation = 
        orderData.orderItemInfo?.cancellation || 
        orderData.orderItemInfo?.status === 'CANCELLED';
      
      // Check orderStatusHistory for CANCELLED status (fallback if orderItemInfo not available)
      const hasCancelledInHistory = orderData.orderItems?.some((item: any) => {
        if (!item.orderStatusHistory || !Array.isArray(item.orderStatusHistory)) {
          return false;
        }
        // Check if any status in history is CANCELLED
        return item.orderStatusHistory.some((historyItem: any) => 
          historyItem.status === 'CANCELLED'
        );
      });
      
      if (hasCancellation || hasCancelledInHistory) {
        // Redirect to cancellation detail page
        navigate(`/shop-services/cancellations/${orderId}/${orderItemId}`, { replace: true });
        return;
      }
      
      setOrderDetail(response.data);
    } catch (err: any) {
      // Provide more specific error messages
      let errorMessage = 'Failed to load order details';
      if (err.response?.status === 404) {
        errorMessage = 'Order not found. Please check the order ID and try again.';
      } else if (err.response?.status === 403) {
        errorMessage = 'You do not have permission to view this order.';
      } else if (err.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
      setRetrying(false);
    }
  };

  const handleRetry = () => {
    fetchOrderDetail(true);
  };

  useEffect(() => {
    fetchOrderDetail();
  }, [orderId, orderItemId]);

  const getCurrentOrderItem = () => {
    if (!orderDetail || !orderDetail.orderItems || orderDetail.orderItems.length === 0) {
      return null;
    }
    
    // First try to find the item marked as isSameItem
    const sameItem = orderDetail.orderItems.find(item => item.isSameItem === true);
    if (sameItem) {
      return sameItem;
    }
    
    // If no isSameItem flag found, try to match with URL orderItemId
    if (orderItemId) {
      const matchingItem = orderDetail.orderItems.find(item => 
        item.productName && item.productName.length > 0
      );
      if (matchingItem) {
        return matchingItem;
      }
    }
    
    // Fallback to first item with valid data
    return orderDetail.orderItems.find(item => item.productName) || orderDetail.orderItems[0];
  };

  const getCurrentStatus = () => {
    const currentItem = getCurrentOrderItem();
    if (!currentItem || !currentItem.orderStatusHistory) return 'CONFIRMED';
    
    // Find all completed statuses (those with updatedAt dates)
    const completedStatuses = currentItem.orderStatusHistory
      .filter(status => status.updatedAt !== null && status.updatedAt !== undefined)
      .sort((a, b) => {
        const dateA = new Date(a.updatedAt!).getTime();
        const dateB = new Date(b.updatedAt!).getTime();
        return dateB - dateA; // Sort by most recent first
      });
    
    // Return the most recent completed status, or 'CONFIRMED' as default
    return completedStatuses.length > 0 ? completedStatuses[0].status : 'CONFIRMED';
  };

  const canMarkAsReady = () => {
    const currentStatus = getCurrentStatus();
    const currentItem = getCurrentOrderItem();
    
    // Can mark action if status is CONFIRMED or READY_FOR_PICKUP and order item exists
    return (currentStatus === 'CONFIRMED' || currentStatus === 'READY_FOR_PICKUP') && currentItem !== null;
  };

  const getActionButtonText = () => {
    const currentStatus = getCurrentStatus();
    switch (currentStatus) {
      case 'CONFIRMED':
        return 'Mark as Ready to Pick Up';
      case 'READY_FOR_PICKUP':
        return 'Mark as Delivered';
      case 'PICKED_UP':
        return 'Mark as Delivered';
      case 'DELIVERED':
        return 'Completed';
      default:
        return 'No Action Available';
    }
  };

  const shouldShowActionButton = () => {
    const currentStatus = getCurrentStatus();
    return ['CONFIRMED', 'READY_FOR_PICKUP', 'PICKED_UP'].includes(currentStatus);
  };

  // Action button handlers
  const handleActionButtonClick = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!hasManagePermissions) return;
    // Prevent double-opening by checking if modal is already showing
    if (!showActionModal) {
      setShowActionModal(true);
    }
  };

  const handleConfirmAction = async () => {
    if (!hasManagePermissions) return;
    const currentStatus = getCurrentStatus();
    const currentItem = getCurrentOrderItem();
    
    if (!currentItem) return;
    
    try {
      setIsProcessingAction(true);
      
      if (currentStatus === 'CONFIRMED') {
        // Mark as ready for pickup
        await markOrdersReadyForPickup([orderItemId || '']);
      } else if (currentStatus === 'READY_FOR_PICKUP') {
        // Mark as delivered (picked up)
        await markOrdersPickedUp([orderItemId || '']);
      }
      
      // Refresh order data
      await fetchOrderDetail();
      setShowActionModal(false);
      
    } catch (error) {
      alert('Failed to process action. Please try again.');
    } finally {
      setIsProcessingAction(false);
    }
  };

  const handleCloseActionModal = () => {
    setShowActionModal(false);
  };

  // Cancel order handlers
  const handleCancelOrder = () => {
    if (!hasManagePermissions) return;
    if (!showCancelModal) {
      setShowCancelModal(true);
    }
  };

  const handleCloseCancelModal = () => {
    if (!isCancelling) {
      setShowCancelModal(false);
    }
  };

  const handleConfirmCancel = async (reason: string) => {
    if (!orderId || !orderItemId) return;
    if (!hasManagePermissions) return;
    
    try {
      setIsCancelling(true);
      
      // Call cancel order API
      await cancelOrder(orderId, orderItemId, reason);
      
      // Close modal
      setShowCancelModal(false);
      
      // Refresh order data - this will trigger redirect to cancellation detail page
      await fetchOrderDetail();
      
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to cancel order. Please try again.';
      alert(errorMessage);
    } finally {
      setIsCancelling(false);
    }
  };

  const canCancelOrder = () => {
    const currentStatus = getCurrentStatus();
    const currentItem = getCurrentOrderItem();
    
    // Can cancel only if status is CONFIRMED and order item exists
    return currentStatus === 'CONFIRMED' && currentItem !== null;
  };

  const getActionModalText = () => {
    const currentStatus = getCurrentStatus();
    if (currentStatus === 'CONFIRMED') {
      return {
        title: 'Mark as Ready for Pickup',
        message: 'Are you sure you want to mark this order as ready for pickup?'
      };
    } else if (currentStatus === 'READY_FOR_PICKUP') {
      return {
        title: 'Mark as Delivered',
        message: 'Are you sure you want to mark this order as delivered?'
      };
    }
    return {
      title: 'Process Order',
      message: 'Are you sure you want to process this order?'
    };
  };

  if (loading) {
    return (
      <div className="page-content order-detail-page" style={{ marginTop: '20px' }}>
        <Container fluid>
          {/* Breadcrumbs Shimmer */}
          <div className="mb-3" style={{ padding: '0 15px' }}>
            <div className="shimmer-breadcrumb" style={{ height: '20px', width: '300px', marginBottom: '20px' }} />
          </div>

          {/* Page Header Shimmer */}
          <div style={{ padding: '0 15px', marginBottom: '24px' }}>
            <div className="shimmer-page-header" style={{ height: '40px', width: '200px' }} />
          </div>

          {/* Order Summary Card Shimmer */}
          <div style={{ padding: '0 15px', marginBottom: '32px' }}>
            <div className="shimmer-order-summary-card" style={{ 
              background: 'white', 
              borderRadius: '12px', 
              padding: '24px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
              border: '1px solid rgba(0, 0, 0, 0.05)'
            }}>
              {/* Order Header Shimmer */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div className="shimmer-circle" style={{ width: '40px', height: '40px', borderRadius: '50%' }} />
                  <div>
                    <div className="shimmer-title" style={{ height: '24px', width: '250px', marginBottom: '4px' }} />
                    <div className="shimmer-date" style={{ height: '16px', width: '120px' }} />
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div className="shimmer-badge" style={{ height: '20px', width: '80px', marginBottom: '8px' }} />
                  <div className="shimmer-order-id" style={{ height: '16px', width: '150px' }} />
                </div>
              </div>

              {/* Track Order Shimmer */}
              <div style={{ 
                borderTop: '1px solid #e9ecef', 
                margin: '0 -24px', 
                padding: '24px',
                background: '#f8f9fa'
              }}>
                <div className="shimmer-track-title" style={{ height: '18px', width: '120px', marginBottom: '24px' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  {[1, 2, 3].map((step) => (
                    <div key={step} style={{ textAlign: 'center', flex: 1 }}>
                      <div className="shimmer-circle" style={{ width: '32px', height: '32px', borderRadius: '50%', margin: '0 auto 12px' }} />
                      <div className="shimmer-step-title" style={{ height: '16px', width: '100px', margin: '0 auto 8px' }} />
                      <div className="shimmer-step-date" style={{ height: '14px', width: '120px', margin: '0 auto 4px' }} />
                      <div className="shimmer-step-badge" style={{ height: '16px', width: '70px', margin: '0 auto' }} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Items Section Shimmer */}
          <div style={{ padding: '0 15px', marginBottom: '32px' }}>
            <div className="row">
              {/* Left Column - Items */}
              <div className="col-lg-6">
                <div style={{ 
                  background: 'white', 
                  borderRadius: '12px', 
                  padding: '32px 24px',
                  marginBottom: '24px'
                }}>
                  <div className="shimmer-items-title" style={{ height: '20px', width: '80px', marginBottom: '24px' }} />
                  <div className="shimmer-item-row" style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
                    <div className="shimmer-item-image" style={{ width: '80px', height: '80px', borderRadius: '8px' }} />
                    <div style={{ flex: 1 }}>
                      <div className="shimmer-item-name" style={{ height: '18px', width: '200px', marginBottom: '8px' }} />
                      <div className="shimmer-item-details" style={{ height: '14px', width: '120px' }} />
                    </div>
                    <div className="shimmer-item-price" style={{ height: '18px', width: '80px' }} />
                  </div>
                  <div className="shimmer-total" style={{ height: '20px', width: '150px', marginTop: '20px' }} />
                  <div className="shimmer-payment" style={{ height: '45px', width: '100%', marginTop: '16px', borderRadius: '12px' }} />
                  <div className="shimmer-seller" style={{ height: '60px', width: '100%', marginTop: '16px', borderRadius: '12px' }} />
                </div>
              </div>

              {/* Right Column - Contact & Pickup */}
              <div className="col-lg-6">
                <div style={{ 
                  background: 'white', 
                  borderRadius: '12px 12px 0 0', 
                  padding: '32px 24px 16px',
                  marginBottom: '0'
                }}>
                  <div className="shimmer-contact-title" style={{ height: '20px', width: '150px', marginBottom: '24px' }} />
                  <div className="shimmer-contact-grid">
                    <div style={{ display: 'flex', gap: '24px', marginBottom: '20px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ height: '14px', width: '100px', marginBottom: '8px' }} />
                        <div style={{ height: '16px', width: '80px' }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ height: '14px', width: '100px', marginBottom: '8px' }} />
                        <div style={{ height: '16px', width: '120px' }} />
                      </div>
                    </div>
                    <div>
                      <div style={{ height: '14px', width: '80px', marginBottom: '8px' }} />
                      <div style={{ height: '16px', width: '200px' }} />
                    </div>
                  </div>
                </div>
                <div style={{ 
                  background: 'white', 
                  borderRadius: '0 0 12px 12px', 
                  padding: '16px 24px 24px'
                }}>
                  <div className="shimmer-pickup-title" style={{ height: '20px', width: '150px', marginBottom: '16px' }} />
                  <div className="shimmer-pickup-details" style={{ 
                    height: '120px', 
                    width: '100%', 
                    background: '#EFF6FF', 
                    borderRadius: '12px',
                    padding: '12px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                      <div style={{ width: '24px', height: '24px', borderRadius: '4px' }} />
                      <div>
                        <div style={{ height: '14px', width: '120px', marginBottom: '4px' }} />
                        <div style={{ height: '16px', width: '100px' }} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '24px', height: '24px', borderRadius: '4px' }} />
                      <div>
                        <div style={{ height: '14px', width: '100px', marginBottom: '4px' }} />
                        <div style={{ height: '16px', width: '180px' }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (error || (!loading && !orderDetail)) {
    return (
      <div className="page-content order-detail-page" style={{ marginTop: '20px' }}>
        <Container fluid>
          {/* Breadcrumbs */}
          <div className="mb-3" style={{ padding: '0 15px' }}>
            <Breadcrumbs items={[
              { label: 'Shop Services', onClick: () => navigate('/shop-services/sellers') },
              { label: 'Orders', onClick: () => navigate(-1) },
              { label: 'Order details', active: true }
            ]} />
          </div>
          
          {/* Error State */}
          <div style={{ padding: '0 15px' }}>
            <div className="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
              <div>
                <i className="ri-error-warning-line me-2"></i>
                {error || 'Order not found'}
              </div>
              <Button 
                variant="outline-danger" 
                size="sm" 
                onClick={handleRetry}
                disabled={retrying}
              >
                {retrying ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Retrying...
                  </>
                ) : (
                  <>
                    <i className="ri-refresh-line me-1"></i>
                    Retry
                  </>
                )}
              </Button>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  const currentOrderItem = getCurrentOrderItem();
  if (!currentOrderItem || !orderDetail) return null;

  return (
    <div className="page-content order-detail-page" style={{ marginTop: '20px' }}>
      <Container fluid>
        {/* Breadcrumbs */}
          <div className="mb-3" style={{ padding: '0 15px' }}>
            <Breadcrumbs items={[
              { label: 'Shop Services', onClick: () => navigate('/shop-services/sellers') },
              { label: 'Orders', onClick: () => navigate(-1) },
              { label: 'Order details', active: true }
            ]} />
          </div>

        {/* Page Header with Back Button */}
        <div style={{ padding: '0 15px', marginBottom: '24px' }}>
          <div className="d-flex align-items-center" style={{ gap: '16px' }}>
            <button 
              className="back-button"
              onClick={() => navigate(-1)}
            >
              <i className="ri-arrow-left-line"></i>
            </button>
            <h1 className="page-title mb-0">Order details</h1>
          </div>
        </div>

        {/* Order Summary Section - White Card */}
        <div style={{ padding: '0 15px', marginBottom: '32px' }}>
          <div className="order-summary-card">
            <div className="order-summary-content">
              <div className="order-summary-header">
                <div className="order-summary-left">
                  <div className="order-summary-icon">
                    <img 
                      src={getImageUrl('admin/static-assets/icons/shop-service/Container.svg')} 
                      alt="Order Container" 
                      className="container-icon"
                    />
                  </div>
                  <div className="order-summary-info">
                    {getCurrentStatus() === 'DELIVERED' || getCurrentStatus() === 'PICKED_UP' ? (
                      <div className="order-summary-title-row">
                        <h4 className="order-summary-title-completed">
                          Order ID: <span className="order-id-text">{orderDetail.id}</span>
                        </h4>
                      </div>
                    ) : (
                      <div className="order-summary-title-row">
                        <h4 className="order-summary-title">{currentOrderItem.productName}</h4>
                        <OrderStatusBadge status={getCurrentStatus()} />
                      </div>
                    )}
                    <p className="order-summary-date">{formatDate(orderDetail.orderDate)}</p>
                  </div>
                </div>
                <div className="order-summary-right">
                  {getCurrentStatus() === 'DELIVERED' || getCurrentStatus() === 'PICKED_UP' ? (
                    <button 
                      className="header-invoice-btn"
                      onClick={async () => {
                        try {
                          const response = await secureApi.get(SHOP_ENDPOINTS.ORDERS.GET_INVOICE(orderId || '', orderItemId || ''));
                          const invoiceUrl = response?.invoiceUrl || response?.data?.invoiceUrl || response?.url || response?.data?.url || response?.link || response?.data?.link;
                          if (invoiceUrl) {
                            window.open(invoiceUrl, '_blank');
                          } else {
                            const blob = new Blob([response], { type: 'application/pdf' });
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `invoice-${orderId}-${orderItemId}.pdf`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(url);
                          }
                        } catch (error) {
                          alert('Error downloading invoice. Please try again.');
                        }
                      }}
                    >
                      Get Invoice
                    </button>
                  ) : (
                    <div className="order-summary-id">
                      Order ID: <span>{orderDetail.id}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Track Order inside the same white card */}
              <div className="order-track-section">
                <TrackOrder 
                  orderStatusHistory={currentOrderItem.orderStatusHistory}
                  currentStatus={getCurrentStatus()}
                  expectedPickupDate={currentOrderItem.expectedPickupDate}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Order Items Section - White Card */}
        <div style={{ padding: '0 15px', marginBottom: '32px' }}>
          <Row className="align-items-stretch">
            {/* Left Column */}
            <Col lg={6} className="d-flex">
              <OrderItems 
                orderItems={[currentOrderItem]}
                paymentMethod={orderDetail.paymentMethod.method}
                sellerName={currentOrderItem.seller}
                orderId={orderId || ''}
                orderItemId={orderItemId || ''}
              />
            </Col>
            
            {/* Right Column */}
            <Col lg={6} className="d-flex">
              <div className="order-detail-right-column">
                <ContactInformation contact={orderDetail.contact} />
                <OrderPickupDetails 
                  sellerLocation={currentOrderItem.sellerLocation}
                  expectedPickupDate={currentOrderItem.expectedPickupDate}
                  pickupLocation={currentOrderItem.pickupLocation}
                />
              </div>
            </Col>
          </Row>
        </div>

        {/* Other Items Section */}
        {orderDetail.orderItems.length > 1 && (
          <div style={{ padding: '0 15px', marginBottom: '32px' }}>
            <OtherOrderItems 
              orderItems={orderDetail.orderItems.filter(item => !item.isSameItem)}
              orderId={orderId || ''}
              onItemClick={(itemOrderId, itemOrderItemId, item) => {
                // Determine navigation based on item status
                const getItemStatus = (orderItem: OrderItemDetail): string => {
                  if (!orderItem.orderStatusHistory || orderItem.orderStatusHistory.length === 0) {
                    return 'CONFIRMED';
                  }
                  
                  // Find all completed statuses (those with updatedAt dates)
                  const completedStatuses = orderItem.orderStatusHistory
                    .filter((status: OrderStatusHistoryItem) => status.updatedAt !== null && status.updatedAt !== undefined)
                    .sort((a: OrderStatusHistoryItem, b: OrderStatusHistoryItem) => {
                      const dateA = new Date(a.updatedAt!).getTime();
                      const dateB = new Date(b.updatedAt!).getTime();
                      return dateB - dateA; // Sort by most recent first
                    });
                  
                  // Return the most recent completed status, or 'CONFIRMED' as default
                  return completedStatuses.length > 0 ? completedStatuses[0].status : 'CONFIRMED';
                };

                const itemStatus = getItemStatus(item);
                
                // Priority 1: Check if item is cancelled
                // Check orderStatusHistory for CANCELLED status
                const hasCancellation = item.orderStatusHistory?.some(
                  (status: OrderStatusHistoryItem) => status.status === 'CANCELLED'
                );
                
                if (itemStatus === 'CANCELLED' || hasCancellation) {
                  navigate(`/shop-services/cancellations/${itemOrderId}/${itemOrderItemId}`);
                  return;
                }
                
                // Priority 2: Check if item has return request
                // Check for returnRequestId, returnStatus, returnRequest, or returnId
                const hasReturn = 
                  item.returnRequestId || 
                  item.returnStatus ||
                  item.returnRequest || 
                  item.returnId;
                
                if (hasReturn) {
                  // Extract returnId from multiple possible locations
                  // Priority: returnRequestId (most common), then returnId, then returnRequest.returnId
                  const returnId = 
                    item.returnRequestId || // Check returnRequestId first (most common in API)
                    item.returnId || 
                    item.returnRequest?.returnId ||
                    item.returnRequest?._id ||
                    item.returnRequest?.id ||
                    (item.returnRequest as any)?._id;
                  
                  if (returnId) {
                    navigate(`/shop-services/returns/${returnId}`);
                    return;
                  }
                }
                
                // Priority 3: Default to order detail page
                navigate(`/shop-services/orders/${itemOrderId}/${itemOrderItemId}`);
              }}
            />
          </div>
        )}

        {/* Sticky Action Bar - Match listing page design */}
        {hasManagePermissions && canMarkAsReady() && (
          <div className="order-detail-actions">
            <Container fluid>
              <div className="order-detail-action-bar-content">
                <Button
                  variant="outline-secondary"
                  className="order-detail-action-cancel"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </Button>
                {canCancelOrder() && (
                  <Button
                    variant="outline-danger"
                    className="order-detail-action-cancel-order"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleCancelOrder();
                    }}
                    disabled={isCancelling || showCancelModal || isProcessingAction}
                  >
                    Cancel Order
                  </Button>
                )}
                <Button
                  variant="primary"
                  className="order-detail-action-confirm"
                  onClick={(e) => handleActionButtonClick(e)}
                  disabled={isProcessingAction || showActionModal || isCancelling || showCancelModal}
                >
                  {isProcessingAction ? 'Processing...' : getActionButtonText()}
                </Button>
              </div>
            </Container>
          </div>
        )}

        {/* Action Confirmation Modal */}
        <MarkReadyModal
          show={showActionModal}
          handleClose={handleCloseActionModal}
          handleConfirm={handleConfirmAction}
          isLoading={isProcessingAction}
          orderCount={1}
          actionType={getCurrentStatus() === 'CONFIRMED' ? 'ready-for-pickup' : 'delivered'}
        />

        {/* Cancel Order Modal */}
        <CancelOrderModal
          show={showCancelModal}
          handleClose={handleCloseCancelModal}
          handleConfirm={handleConfirmCancel}
          isLoading={isCancelling}
        />
      </Container>
    </div>
  );
};

export default OrderDetail;